/* eslint-disable @typescript-eslint/naming-convention */
import { request, cookie, elk, CustomException, language } from '@tencent/ppd-base-libs';
import { openHKOfflinePayView, OpenHKOfflinePayViewParams } from '../utils/wxapi';
import { to } from '@tencent/wphk-components-common/utils/common';
import { jumpUrl } from './jump-page';
import { userEnv } from '@tencent/wphk-base-utils';
import { showBusinessError, showDialog } from '@tencent/wphk-components-dialog';
import { i18n } from '../i18n/index';

/** 跳转刷卡h5页面地址 */
const SCANPAY_H5_URL = '//hkwallet.moneydata.hk/fcgi-bin/app/ewallet_bindcard/ewallet_qrpay_index.fcgi';
/** 营销活动默认host */
const QRPAY_PROM_HOST = 'prom.wechatpay.com.hk';
/** 优惠券已过期错误码标识 */
const COUPON_USED_ERROR_CODE = '1613222521';
/** 拉起刷卡异常枚举 */
export enum JumpScanpayExeption {
  /** 券已过期 */
  CouponUsed = 'couponUsedException',
  /** jsapi拉起原生刷卡失败 */
  JsapiInvokeFailed = 'launchScanpayException',
}

/**
 * 拉起刷卡场景
 */
export enum QrpayFromScene {
  /**
   * 基础钱包
   * 对应AppID：wx8d9c8a87488fa38a
   */
  HKwallet = 1,
  /**
   * 运营活动
   * 对应AppID：wxd82e1d3dfccffe5e
   * */
  Offers = 2,
  /**
   * 好生活
   * 对应AppID：wx9a52ebdae52f710f
   */
  HKLife = 3,
  /**
   * 专属奖赏小程序
   * 对应APPID：wxcdaffda0606152a0
   */
  ZSJSMp = 4,
}

/**
 * 优惠信息
 */
type PrizeNoInfo = {
  /** 优惠券ID */
  couponId?: string;
  /** 消费券ID */
  cvPrizeNo?: string;
};

/**
 * 跳转刷卡参数
 */
type JumpScanpayOptions = {
  /**
   * 刷卡场景
   */
  scene?: QrpayFromScene;
  /**
   * 是否上报sdk调用日志
   */
  isReportLog?: boolean;
};

/**
 * 获取拉起原生刷卡需要的参数，同时检测当前用户是否支持原生刷卡支付
 * @param params 拉起刷卡携带的参数(可以带一个消费券ID、或优惠券ID)
 * @param scene 场景类型，对应不同的公众号)
 */
async function prepareQrpay(params: PrizeNoInfo, scene?: QrpayFromScene) {
  const url = '//prom.wechatpay.com.hk/app/qrpay/prepare_qrpay.cgi';
  const { couponId, cvPrizeNo } = params;
  // 支持通过指定优惠券或消费券拉起原生刷卡
  // 目前没有同时指定2者的场景，所以同时传递2者的情况下，优先使用优惠券拉起原生
  let idData = {};
  if (couponId) {
    idData = { prize_no: couponId };
  } else if (cvPrizeNo) {
    idData = { cv_prize_no: cvPrizeNo };
  }

  const from = getFromScene(scene); // 标示运营活动场景
  // 如果请求页面和CGI不在同一域，需要传递登陆态
  let qlData = {};
  if (!url.includes(location.host)) {
    qlData = {
      qluin: cookie.get('qluin'),
      qlskey: cookie.get('qlskey'),
    };
  }
  const [err, res] = await to<{
    retcode: string,
    retmsg: string
  }>(request.post(url, { from, ...idData, ...qlData }));
  if (err) {
    // 券已使用，不能重复用券进行拉码支付
    if (err.retcode === COUPON_USED_ERROR_CODE) {
      throw new CustomException(err, JumpScanpayExeption.CouponUsed, err.retmsg || 'this coupon has been used');
    }
    throw err;
  }
  const { app_id, time_stamp, nonce_str, pay_sign, sign_type, package_info, support_native } = res;
  const wxConfig: OpenHKOfflinePayViewParams = {
    appId: app_id as string,
    timeStamp: time_stamp as string, // 时间戳
    nonceStr: nonce_str as string, // 随机字符串
    paySign: pay_sign as string, // 签名
    signType: sign_type as string, // 签名方式（SHA1）
    package: package_info ? atob(package_info) : '{}', // 扩展字符串，由商户按规范拼接
  };
  return {
    isSupportNative: support_native === '1', // 判断是否支持原生刷卡
    wxConfig,
  };
}

/**
 * 获取拉起刷卡场景
 * @param scene 场景
 * @returns
 */
function getFromScene(scene?: QrpayFromScene) {
  if (scene) {
    return scene;
  }
  if (QRPAY_PROM_HOST !== location.host) {
    // prom以外的域名，只能是来自基础钱包业务
    return QrpayFromScene.HKwallet;
  }
  return QrpayFromScene.Offers;
}

/**
 * 上报日志
 * @param key 调用方法
 * @param data 方法调用时传入参数
 */
function reportLog(key: string, data?: any) {
  const body = data || {};
  elk({ key, body });
  console.log('jsapi invoke:', key, '\ndata:', JSON.stringify(body));
}

/**
 *  拉起原生刷卡
 * @param wxConfig 接口返回的微信签名数据
 * @param report 是否上报日志
 */
async function launchScanpay(wxConfig: OpenHKOfflinePayViewParams, report = true) {
  if (report) {
    reportLog('proxy_jsapi_before_call_openHKOfflinePayView', wxConfig);
  }
  const [err, resp] = await to(openHKOfflinePayView(wxConfig));
  if (report) {
    reportLog('proxy_jsapi_call_openHKOfflinePayView', resp);
  }
  if (err) {
    throw new CustomException(err, JumpScanpayExeption.JsapiInvokeFailed, 'launchScanpay failed');
  }
}

/**
 * H5：尝试拉起原生刷卡界面、小程序：拉起小程序中转页面
 * @param params 拉起原生刷卡可以携带优惠券(couponId)、消费券(cvPrizeNo)参数
 * @param options 扩展参数
 * @returns  true：支持原生刷卡，直接拉起； false： 不支持原生，跳转h5刷卡页兜底
 */
async function tryLaunchScanpay(params: PrizeNoInfo = {}, options: JumpScanpayOptions = { isReportLog: true }) {
  // 小程序处理
  if (isInMiniProgram()) {
    jumpScanpayInMp(params);
    return true;
  }
  const { isReportLog = true, scene } = options;
  const { isSupportNative, wxConfig } = await prepareQrpay(params, scene);
  if (isSupportNative) {
    await launchScanpay(wxConfig, isReportLog);
    return true;
  }
  return false;
}

/**
 * 通过优惠券拉起刷卡支付
 * @param couponId 优惠券ID
 * @param adtag adtag来源标示
 * @param options 扩展参数
 */
export async function jumpScanpayByCouponId(couponId: string, adtag: string, options?: JumpScanpayOptions) {
  const result = await tryLaunchScanpay({ couponId }, options);
  if (!result) {
    jumpUrl(SCANPAY_H5_URL, { params: { aff_type: '1', aff_value: couponId }, adtag });
  }
}

/**
 * 通过消费券拉起刷卡支付
 * @param cvId 消费券ID
 * @param adtag adtag来源标示
 * @param options 扩展参数
 */
export async function jumpScanpayByCVId(cvId: string, adtag: string, options?: JumpScanpayOptions) {
  const result = await tryLaunchScanpay({ cvPrizeNo: cvId }, options);
  if (!result) {
    jumpUrl(SCANPAY_H5_URL, { params: { cv_prize_no: cvId }, adtag });
  }
}

/**
 * 直接拉起刷卡支付，不带任何优惠参数
 * @param url 用户不支持原生刷卡后跳转的h5刷卡地址（需要用户指定）
 * @param adtag 来源标示
 * @param options 扩展参数
 */
export async function jumpScanpay(url: string, adtag: string, options?: JumpScanpayOptions) {
  const result = await tryLaunchScanpay({}, options);
  if (!result && url) {
    jumpUrl(url, { adtag });
  }
}

/** 可以拉起原生刷卡的小程序appid列表 */
const SCANPAY_MP_INFO: Array<{ appId: string; scene: QrpayFromScene }> = [
  {
    appId: 'wxcdaffda0606152a0',
    scene: QrpayFromScene.ZSJSMp,
  },
];

/**
 * 判断 H5 是否在小程序环境中
 */
function isInMiniProgram() {
  return userEnv.isInMiniProgram();
}

/**
 * 根据 appid 获取当前所处小程序信息
 * @param appId 小程序 appId
 */
function getCurrentMpInfo(appId: string) {
  return SCANPAY_MP_INFO.find(ele => ele.appId === appId);
}

/**
 * H5被非白名单小程序内嵌，需要弹窗提示不支持在该小程序中拉起付款码
 */
function showInvalidMpDialog() {
  const lang = language.getLanguage();
  showDialog({
    title: '',
    htmlContent: `
      <div class="${lang === 'en' ? 'font-30-medium' : 'font-34-medium'}" style="margin-bottom:0.3rem">${i18n('暫不支援在該小程式中打開')}</div>
      <div style="width:100%;min-height:1.4rem"><img style="width:100%;" src="${i18n('guide')}" /></div>
      <div class="${lang === 'en' ? 'font-28-regular' : 'font-32-regular'}" style="color:#19191980;margin-top:0.15rem">${i18n('請參考以上指引，通過微信/WeChat右上角「+」打開「收付款」')}</div>
    `,
    btns: [{
      text: i18n('我知道了'),
    }],
  });
}

/**
 * 处理H5被小程序内嵌时，拉起原生刷卡
 * 逻辑：
 * 1.H5先跳转到小程序原生中转页面
 * 2.在小程序原生中转页面中，用户点击按钮拉起付款码（目前在专属奖赏小程序代码中实现了小程序原生中转页面）
 */
async function jumpScanpayInMp(params: PrizeNoInfo = {}) {
  // 1. 获取小程序信息
  const appId = userEnv.getMiniProgramAppId();
  const currentMpInfo = getCurrentMpInfo(appId);
  // 2. H5被非白名单小程序内嵌，需要弹窗提示不支持在该小程序中拉起付款码
  if (!currentMpInfo) {
    showInvalidMpDialog();
    elk({ key: 'JumpScanpayInMpBlock', str1: appId, msg: currentMpInfo });
    return;
  }
  // 3. 获取支付签名数据
  const [err, data] = await to(prepareQrpay(params, currentMpInfo.scene));
  if (err || !data) {
    showBusinessError(err);
    return;
  }
  const { wxConfig } = data;
  const wxConfigStr = JSON.stringify(wxConfig);
  // 4. 拉起小程序原生中转页面
  wx.miniProgram.navigateTo({
    url: `/pages/pay/pay?scanpayParmas=${encodeURIComponent(wxConfigStr)}`,
    success: () => {
      elk({ key: 'JumpScanpayInMpSuccess', msg: wxConfigStr });
    },
    fail: () => {
      new CustomException(wxConfigStr, 'JumpScanpayInMpFail', '跳转小程序拉起付款码中转页失败');
    },
  });
}
